import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Twitter, Github } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navigation = [
    { name: 'About us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Career', path: '/career' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    { name: 'Artificial Intelligence', path: '/services' },
    { name: 'Machine Learning & Big Data', path: '/services' },
    { name: 'AI-Assisted Legacy Code Dev', path: '/services' },
    { name: 'Enterprise Application Dev', path: '/services' },
    { name: 'Full Stack Development', path: '/services' },
    { name: 'Business Analytics', path: '/services' },
    { name: 'Legacy Software', path: '/services' }
  ];

  const support = [
    { name: 'Privacy Policy', path: '#' },
    { name: 'Terms of Service', path: '#' },
    { name: 'Cookies', path: '#' }
  ];

  return (
    <footer className="relative bg-gray-900 text-white overflow-hidden">
      <div className="container-max section-padding pt-20 pb-8 relative z-10">
        {/* Link columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-16">
          {/* Get in Touch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-gray-400 text-sm font-medium mb-6 uppercase tracking-wider">Get in Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-primary-400 mt-1 flex-shrink-0" />
                <a href="mailto:signup@newprodata.com" className="text-gray-200 hover:text-primary-400 transition-colors text-sm">
                  signup@newprodata.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-4 h-4 text-primary-400 mt-1 flex-shrink-0" />
                <a href="tel:+19087675132" className="text-gray-200 hover:text-primary-400 transition-colors text-sm">
                  +1 908 767 5132
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-primary-400 mt-1 flex-shrink-0" />
                <span className="text-gray-200 text-sm leading-relaxed">
                  24-1, 2nd St, S S Colony,<br />
                  Madurai, Tamil Nadu 625016
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-gray-400 text-sm font-medium mb-6 uppercase tracking-wider">Navigation</h3>
            <ul className="space-y-4">
              {navigation.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-200 hover:text-primary-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-gray-400 text-sm font-medium mb-6 uppercase tracking-wider">Services</h3>
            <ul className="space-y-4">
              {services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-gray-200 hover:text-primary-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Support & Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-gray-400 text-sm font-medium mb-6 uppercase tracking-wider">Support</h3>
            <ul className="space-y-4 mb-8">
              {support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.path}
                    className="text-gray-200 hover:text-primary-400 transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            <h3 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wider">Follow Us</h3>
            <div className="flex space-x-4">
              <a href="#" aria-label="LinkedIn" className="text-gray-300 hover:text-primary-400 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" aria-label="Twitter" className="text-gray-300 hover:text-primary-400 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" aria-label="GitHub" className="text-gray-300 hover:text-primary-400 transition-colors">
                <Github className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Giant fading wordmark — SVG so it always fits the container width */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2 }}
          className="relative w-full select-none pointer-events-none mb-4"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 1000 220"
            preserveAspectRatio="xMidYMid meet"
            className="w-full block"
          >
            <defs>
              <linearGradient id="footerWordFade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9CA3AF" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#4B5563" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#111827" stopOpacity="0" />
              </linearGradient>
            </defs>
            <text
              x="500"
              y="180"
              textAnchor="middle"
              textLength="960"
              lengthAdjust="spacingAndGlyphs"
              fontSize="200"
              fontWeight="900"
              fontFamily="inherit"
              fill="url(#footerWordFade)"
            >
              NEWPRODATA
            </text>
          </svg>
        </motion.div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 pt-6 mt-4 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 relative z-10"
        >
          <p className="text-gray-500 text-xs sm:text-sm">
            © {currentYear} NewProData. All rights reserved.
          </p>
          <p className="text-gray-500 text-xs sm:text-sm">
            Crafted with care in Madurai.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
